import { AccessibleDataTableColumn } from '@aidkitorg/component-library';
import { DashboardSummaryTable, DashboardComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import * as XLSX from 'xlsx';
import { getTodayAsYYYYMMDD, toSnakeCase } from '../../../utils/adminPanel.utils';

type ExportColumn = Pick<AccessibleDataTableColumn, 'key' | 'label'>;

/**
 * Maps raw table rows to export rows keyed by human-readable column labels.
 * Falls back to prettified data keys when no columns match the data.
 */
export function buildExportRows(
  data: Record<string, any>[],
  columns: ExportColumn[],
): Record<string, any>[] {
  if (data.length === 0) return [];

  const dataKeys = Object.keys(data[0]);

  const keyToLabel: Record<string, string> = {};
  columns.forEach((col) => {
    if (col.key && dataKeys.includes(col.key)) {
      keyToLabel[col.key] = col.label;
    }
  });

  const keysToExport = Object.keys(keyToLabel).length > 0
    ? Object.keys(keyToLabel)
    : dataKeys;

  return data.map((row) => {
    const exportRow: Record<string, any> = {};
    keysToExport.forEach((key) => {
      const label = keyToLabel[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      exportRow[label] = row[key] ?? '';
    });
    return exportRow;
  });
}

export function getExportFileName(title: string, extension: string): string {
  return `${toSnakeCase(title)}_${getTodayAsYYYYMMDD()}.${extension}`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Generate and download a CSV file from table data. */
export function downloadTableAsCSV(
  data: Record<string, any>[],
  columns: ExportColumn[],
  title: string,
): void {
  const exportData = buildExportRows(data, columns);
  if (exportData.length === 0) return;

  const csv = [Object.keys(exportData[0])]
    .concat(exportData.map((obj) => Object.values(obj)))
    .map((row) => row.map(String).map((val) => `"${val.replace(/"/g, '""')}"`).join(','))
    .join('\n');

  triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), getExportFileName(title, 'csv'));
}

/** Generate and download an XLSX file from table data. */
export function downloadTableAsXLSX(
  data: Record<string, any>[],
  columns: ExportColumn[],
  title: string,
  sheetName = 'Data',
): void {
  const exportData = buildExportRows(data, columns);
  if (exportData.length === 0) return;

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, getExportFileName(title, 'xlsx'));
}

/**
 * Derives the export columns (key + label) for a Summary Table, mirroring the
 * column logic in SummaryTable.tsx so the download matches the rendered table.
 * Keep in sync with SummaryTable.tsx if that column derivation changes.
 */
export function buildSummaryTableExportColumns(
  component: DashboardSummaryTable,
  result: DashboardComponentResult | undefined,
  opts?: { applicantUidLabel?: string },
): AccessibleDataTableColumn[] {
  if (!result?.data || !Array.isArray(result.data) || result.data.length === 0) {
    return [];
  }

  const allKeys = new Set<string>();
  result.data.forEach((row: Record<string, any>) => {
    Object.keys(row).forEach((key) => allKeys.add(key));
  });

  const formatColumnHeader = (field: string): string => {
    if (field === '__synthetic_uid__') {
      return opts?.applicantUidLabel ?? 'Applicant UID';
    }
    return field
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const columns: AccessibleDataTableColumn[] = [];

  component.groups.forEach((group) => {
    if (group.kind === 'Field') {
      const actualFieldName = group.field === '__synthetic_uid__' ? 'uid' : group.field;
      if (allKeys.has(actualFieldName)) {
        columns.push({ key: actualFieldName, label: formatColumnHeader(group.field) });
      }
    }
  });

  component.summary.forEach((summaryExpr, index) => {
    if (summaryExpr.kind === 'Count') {
      const summaryKey = result.summaryKey || 'count';
      if (allKeys.has(summaryKey)) {
        columns.push({ key: summaryKey, label: 'Count' });
      }
    } else {
      const summaryKey = result.summaryKey || `summary_${index}`;
      if (allKeys.has(summaryKey)) {
        const headerName = summaryExpr.kind === 'Sum' ? 'Sum'
          : summaryExpr.kind === 'Mean' ? 'Average'
            : 'Value';
        columns.push({ key: summaryKey, label: headerName });
      }
    }
  });

  return columns;
}
